import { isAxiosError } from 'axios'

import { handleException } from 'clients/exception-handling'
import type { ApiClient } from 'clients/yogi-api'
import {
  TransferEntities,
  TransferEntityNames,
} from 'features/products/helpers'
import type {
  Product,
  ProductGroupList,
  ProductHierarchyInfo,
  ProductHierarchyList,
  ProductTransferEntity,
} from 'features/products/types'
const PRODUCT_HIERARCHY_INFO = '/product/hierarchy/info'
const PRODUCT_PROJECT_INFO = '/product/project/info'
const PRODUCT_HIERARCHY = '/product/hierarchy'
const PRODUCT_PROJECT_HIERARCHIES = '/product/project/hierarchies'
const PRODUCT_LIST = '/product/hierarchy/productlist'
const PRODUCT_GROUPS = '/product/group'
const PRODUCT_GROUP_PRODUCTS = '/product/group/products'
const PRODUCT_HIERARCHY_PRODUCTS = '/product/hierarchy/products'

export class ProductService {
  constructor(private client: ApiClient) {}

  async getProductHierarchy(
    _key: string,
    proj_uuid: string,
  ): Promise<ProductHierarchyList> {
    const { data } = await this.client.get<ProductHierarchyList>(
      PRODUCT_HIERARCHY,
      {
        params: {
          proj_uuid: proj_uuid,
        },
      },
    )
    return data
  }

  async getProjectHierarchyInfo(
    _key: string,
    proj_uuid: string,
    hierarchy: string,
  ): Promise<ProductHierarchyInfo> {
    const { data } = await this.client.get<ProductHierarchyInfo>(
      PRODUCT_HIERARCHY_INFO,
      {
        params: {
          proj_uuid: proj_uuid,
          hierarchy: hierarchy,
        },
      },
    )
    return data
  }

  async getProductHierarchyAll(
    _key: string,
    proj_uuid: string,
  ): Promise<ProductHierarchyInfo[]> {
    const { data } = await this.client.get<ProductHierarchyInfo[]>(
      PRODUCT_PROJECT_INFO,
      {
        params: {
          proj_uuid: proj_uuid,
        },
      },
    )
    return data
  }

  async getProductHierarchyAllOld(
    _key: string,
    proj_uuid: string,
  ): Promise<ProductHierarchyInfo[]> {
    const { data: hieararchyNames } =
      await this.client.get<ProductHierarchyList>(PRODUCT_HIERARCHY, {
        params: {
          proj_uuid: proj_uuid,
        },
      })
    const data: ProductHierarchyInfo[] = []
    await Promise.all(
      hieararchyNames.product_hierarchies.map((obj) =>
        this.client
          .get<ProductHierarchyInfo>(PRODUCT_HIERARCHY_INFO, {
            params: {
              proj_uuid: proj_uuid,
              hierarchy: obj,
            },
          })
          .then((response) => {
            data.push(response.data)
          }),
      ),
    )
    return data
  }

  async getProductHierarchiesForProject(
    _key: string,
    proj_uuid: string,
    dateMin?: number,
    dateMax?: number,
    recordType?: string,
  ): Promise<ProductHierarchyInfo[]> {
    const { data } = await this.client.get<ProductHierarchyInfo[]>(
      PRODUCT_PROJECT_HIERARCHIES,
      {
        params: {
          proj_uuid: proj_uuid,
          date_min: dateMin,
          date_max: dateMax,
          record_type: recordType,
        },
      },
    )
    return data
  }

  async getProductsForHierarchy(
    _key: string,
    proj_uuid: string,
    hierarchy: string,
    dateMin?: number,
    dateMax?: number,
    recordType?: string,
  ): Promise<Product[]> {
    try {
      const { data } = await this.client.get<Product[]>(PRODUCT_LIST, {
        params: {
          proj_uuid: proj_uuid,
          hierarchy: hierarchy,
          date_min: dateMin,
          date_max: dateMax,
          record_type: recordType,
        },
      })
      return data
    } catch (error: unknown) {
      if (isAxiosError(error) && error.response?.status === 404) {
        handleException(error, {
          operation: 'products.list',
          disposition: 'expected',
          isExpectedError: (cause) =>
            isAxiosError(cause) && cause.response?.status === 404,
          reason:
            'A missing resource is represented by the existing empty/not-found result.',
        })
        return []
      }
      throw error
    }
  }

  async fetchProductGroups(proj_uuid: string): Promise<ProductGroupList> {
    const { data } = await this.client.get<ProductGroupList>(PRODUCT_GROUPS, {
      params: { proj_uuid },
    })
    return data
  }

  async fetchDataForProductEditing(
    proj_uuid: string,
  ): Promise<ProductHierarchyList & ProductGroupList> {
    const [hierarchies, groups] = await Promise.all([
      this.getProductHierarchy('', proj_uuid),
      this.fetchProductGroups(proj_uuid),
    ])

    return {
      ...hierarchies,
      ...groups,
    }
  }

  async fetchProductsForGroupOrHierarchy(
    proj_uuid: string,
    entity: string,
    type: TransferEntities,
  ): Promise<ProductTransferEntity[]> {
    const isGroup = type === TransferEntities.GROUP
    const url = isGroup ? PRODUCT_GROUP_PRODUCTS : PRODUCT_HIERARCHY_PRODUCTS

    const { data } = await this.client.get<{ items: ProductTransferEntity[] }>(
      url,
      {
        params: { proj_uuid, [TransferEntityNames[type]]: entity },
      },
    )

    return data.items
  }

  async updateProductsForGroupOrHierarchy(
    proj_uuid: string,
    entity: string,
    product_uuids: string[],
    type: TransferEntities,
  ): Promise<void> {
    const isGroup = type === TransferEntities.GROUP
    const url = isGroup ? PRODUCT_GROUPS : PRODUCT_HIERARCHY

    await this.client.put(
      url,
      { [TransferEntityNames[type]]: entity, product_uuids },
      { params: { proj_uuid } },
    )
  }
}
